import { Dropdown, Form } from 'antd';
import { FormInstance } from 'antd/es/form/hooks/useForm';
import { MouseEvent as ReactMouseEvent, useState } from 'react';

import { SequenceCard, SequenceCardWrapper, SequenceTitleWrapper } from '@page/new-sequences/new-sequences.style';

import { Button } from '@atom/button/button';
import { ButtonTypeEnum } from '@atom/button/button.enum';
import { ServiceIcon } from '@atom/service-icon/service-icon';
import { Text, Title } from '@atom/typography/typography';
import { ConnectorServiceEnum } from '@enum/connector-service.enum';
import { ColorEnum } from '@enum/style/color.enum';
import { UNIFIED_APIS, UnifiedApiEnumNames } from '@enum/unified-api.enum';
import { Label } from '@form/connector-auth/connector-auth.form-style';
import { useStoreSelector } from '@hook/store/store.hooks';
import { isExist, isString } from '@util/data/is-exist';

import ConnectorIcon from '../assets/connector.svg?react';
import PlusIcon from '../assets/plus.svg?react';
import {
  ConnectionButton,
  Dots,
  ModalConnections,
  ModalConnectionsCol,
  ModalConnectionsWrapper,
} from './sequence-connection.style';

interface SequenceConnectionProps {
  form: FormInstance;
}

export const SequenceConnection = ({ form }: SequenceConnectionProps) => {
  const { data, isLoading } = useStoreSelector((state) => state.connectors);
  const [isOpened, setIsOpened] = useState(false);
  const serviceId = Form.useWatch('serviceId', form);

  const connections = Object.values(data);
  const currentService: any = isString(serviceId)
    ? connections.find((connection: any) => connection.serviceId === serviceId)
    : {};
  const isExistService = isExist(currentService);
  const handleConnection =
    (serviceId: ConnectorServiceEnum | null) => (event: ReactMouseEvent<HTMLElement, MouseEvent>) => {
      event.preventDefault();
      event.stopPropagation();
      form.setFieldsValue({ serviceId });
      setIsOpened(false);
    };

  return (
    <>
      <SequenceCard>
        <SequenceCardWrapper onClick={() => setIsOpened(true)}>
          {isExistService ? <ServiceIcon icon={currentService.serviceId} width={24} /> : <ConnectorIcon />}
          <SequenceTitleWrapper>
            <Title color={ColorEnum.Black100} level={6}>
              {isExistService ? currentService.name : '1. Connection'}
            </Title>
            <Text color={ColorEnum.Black70} level='s'>
              {isExistService
                ? (UnifiedApiEnumNames as any)[currentService.unifiedApi]
                : 'Start by adding your connection.'}
            </Text>
          </SequenceTitleWrapper>
          {isExistService ? (
            <Dropdown
              placement='bottomRight'
              menu={{
                items: [
                  {
                    key: 'clear',
                    label: (
                      <Button size='small' type={ButtonTypeEnum.Error} onClick={handleConnection(null)}>
                        Reset
                      </Button>
                    ),
                  },
                ],
              }}
            >
              <Dots />
            </Dropdown>
          ) : (
            <PlusIcon />
          )}
        </SequenceCardWrapper>
      </SequenceCard>
      <ModalConnections
        confirmLoading={false}
        footer={null}
        loading={isLoading}
        open={isOpened}
        title='Add connection'
        centered
        onCancel={() => setIsOpened(false)}
      >
        <ModalConnectionsWrapper>
          {UNIFIED_APIS.map(([title, key]) => (
            <ModalConnectionsCol key={key}>
              <Title color={ColorEnum.Black100} level={6} weight={600}>
                {title}
              </Title>
              {connections
                .filter((connection: any) => connection.unifiedApi === key)
                .map((connection: any) => (
                  <ConnectionButton
                    key={connection.unifiedApi}
                    type={ButtonTypeEnum.Link}
                    onClick={handleConnection(connection.serviceId)}
                  >
                    <ServiceIcon icon={connection.serviceId} width={24} />
                    <Text color={ColorEnum.Inherit} level='s' weight={500}>
                      {connection.name}
                    </Text>
                  </ConnectionButton>
                ))}
            </ModalConnectionsCol>
          ))}
        </ModalConnectionsWrapper>
        <Label name='serviceId' style={{ display: 'contents' }} />
      </ModalConnections>
    </>
  );
};
